import React, { useState, useEffect } from 'react';
import { Button, Modal, Loader } from '@mantine/core';
import apiService from '../../services/api';
import type { TrainerEarningsDashboardProps, EarningsSummary } from '../../types';

interface PaymentRecord {
  id: string;
  session_id: string;
  created_at: string;
  gross_amount: number;
  platform_fee: number;
  net_amount: number;
  payment_status: string;
  [key: string]: unknown;
}

interface EarningsDataExtended extends EarningsSummary {
  payment_count?: number;
  available_balance?: number;
  pending_balance?: number;
  first_payment_date?: string;
  last_payment_date?: string;
}

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'paid': return 'bg-green-500';
    case 'pending': return 'bg-[#f39c12]';
    case 'failed': return 'bg-red-500';
    default: return 'bg-gray-500';
  }
};

const TrainerEarningsDashboard: React.FC<TrainerEarningsDashboardProps> = ({ trainerId }) => {
  const [earningsData, setEarningsData] = useState<EarningsDataExtended | null>(null);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showPayoutModal, setShowPayoutModal] = useState<boolean>(false);
  const [payoutAmount, setPayoutAmount] = useState<number>(0);
  const [pendingPayments, setPendingPayments] = useState<PaymentRecord[]>([]);
  const [isStripeConnect, setIsStripeConnect] = useState<boolean>(false);

  useEffect(() => {
    loadEarningsData();
    loadPayments();
  }, [trainerId, selectedYear]);

  const loadEarningsData = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await apiService.getTrainerEarningsSummary(trainerId, selectedYear) as Record<string, unknown>;

      if (response.Code === 200) {
        setEarningsData(response.data as EarningsDataExtended);
        if (response.source === 'stripe') {
          setIsStripeConnect(true);
        }
      } else {
        setError('Failed to load earnings data');
      }
    } catch (err) {
      console.error('Error loading earnings data:', err);
      setError('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async (): Promise<void> => {
    try {
      const response = await apiService.getTrainerPayments(trainerId) as Record<string, unknown>;

      if (response.Code === 200) {
        const paymentData = response.data as PaymentRecord[];
        setPayments(paymentData);

        // Calculate pending payments
        const pending = paymentData.filter((payment: PaymentRecord) => payment.payment_status === 'pending');
        setPendingPayments(pending);
        setPayoutAmount(pending.reduce((sum: number, payment: PaymentRecord) => sum + payment.net_amount, 0));
      }
    } catch (err) {
      console.error('Error loading payments:', err);
    }
  };

  const handlePayoutRequest = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await apiService.processTrainerPayout(trainerId, {
        payout_reference: `payout_${Date.now()}`
      }) as Record<string, unknown>;

      if (response.Code === 200) {
        alert('Payout request submitted successfully!');
        setShowPayoutModal(false);
        loadPayments();
        loadEarningsData();
      } else {
        setError((response.Message as string) || 'Failed to process payout');
      }
    } catch (err) {
      console.error('Error processing payout:', err);
      setError('Failed to process payout');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && !earningsData) {
    return (
      <div className="font-sans bg-white/95 rounded-2xl p-4 sm:p-6 md:p-8 max-w-[1200px] mx-auto shadow-[0_10px_30px_rgba(0,0,0,0.1)] border border-white/20">
        <div className="text-center text-[#f39c12] text-lg p-10">
          <Loader color="orange" size="lg" />
          <div className="mt-4">Loading earnings data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans bg-white/95 rounded-2xl p-4 sm:p-6 md:p-8 max-w-[1200px] mx-auto shadow-[0_10px_30px_rgba(0,0,0,0.1)] border border-white/20">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-8 pb-5 border-b-2 border-gray-200">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#2c3e50] m-0">Earnings Dashboard</h2>
        <div className="flex items-center gap-2.5">
          <label className="font-semibold text-[#2c3e50]">Year:</label>
          <select
            value={selectedYear}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedYear(parseInt(e.target.value))}
            className="p-2 px-3 rounded-lg border-2 border-gray-300 text-base font-semibold text-[#2c3e50] bg-white"
          >
            {[2024, 2025, 2026].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500 text-red-500 p-4 rounded-xl mb-5 text-center">{error}</div>}

      {/* Earnings Summary Cards */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-5 mb-8">
        <div className="bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white p-4 sm:p-6 rounded-2xl text-center shadow-[0_8px_25px_rgba(102,126,234,0.3)]">
          <div className="text-base font-semibold mb-2.5 opacity-90">Total Earnings ({selectedYear})</div>
          <div className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1">
            {formatCurrency(earningsData?.total_earnings || 0)}
          </div>
          <div className="text-sm opacity-80">
            {earningsData?.payment_count || 0} sessions
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white p-4 sm:p-6 rounded-2xl text-center shadow-[0_8px_25px_rgba(102,126,234,0.3)]">
          <div className="text-base font-semibold mb-2.5 opacity-90">
            {isStripeConnect ? 'Available Balance' : 'Pending Payout'}
          </div>
          <div className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1">
            {isStripeConnect
              ? formatCurrency(earningsData?.available_balance || 0)
              : formatCurrency(payoutAmount)}
          </div>
          <div className="text-sm opacity-80">
            {isStripeConnect
              ? `${formatCurrency(earningsData?.pending_balance || 0)} pending`
              : `${pendingPayments.length} payments`}
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white p-4 sm:p-6 rounded-2xl text-center shadow-[0_8px_25px_rgba(102,126,234,0.3)]">
          <div className="text-base font-semibold mb-2.5 opacity-90">First Payment</div>
          <div className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1">
            {earningsData?.first_payment_date ?
              formatDate(earningsData.first_payment_date) : 'N/A'}
          </div>
          <div className="text-sm opacity-80">
            {selectedYear}
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white p-4 sm:p-6 rounded-2xl text-center shadow-[0_8px_25px_rgba(102,126,234,0.3)]">
          <div className="text-base font-semibold mb-2.5 opacity-90">Last Payment</div>
          <div className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1">
            {earningsData?.last_payment_date ?
              formatDate(earningsData.last_payment_date) : 'N/A'}
          </div>
          <div className="text-sm opacity-80">
            {selectedYear}
          </div>
        </div>
      </div>

      {/* Payout Section */}
      {isStripeConnect ? (
        <div className="bg-[#635bff]/10 border-2 border-[#635bff] rounded-2xl p-6 mb-8 text-center">
          <h3 className="text-xl sm:text-2xl font-bold text-[#635bff] mb-4">Automatic Payouts</h3>
          <p className="text-gray-500 mb-2.5">
            Your earnings are automatically deposited to your connected bank account on Stripe's payout schedule.
          </p>
        </div>
      ) : payoutAmount > 0 ? (
        <div className="bg-green-500/10 border-2 border-green-500 rounded-2xl p-6 mb-8 text-center">
          <h3 className="text-xl sm:text-2xl font-bold text-[#2c3e50] mb-4">Ready for Payout</h3>
          <div className="text-xl sm:text-2xl md:text-3xl font-bold text-green-500 mb-5">
            {formatCurrency(payoutAmount)}
          </div>
          <p className="text-gray-500 mb-5">
            You have {pendingPayments.length} pending payment{pendingPayments.length !== 1 ? 's' : ''} ready for payout.
          </p>
          <Button
            onClick={() => setShowPayoutModal(true)}
            disabled={loading}
            color="green"
            radius="md"
            size="lg"
          >
            Request Payout
          </Button>
        </div>
      ) : null}

      {/* Payments Table */}
      <div>
        <h3 className="text-xl sm:text-2xl font-bold text-[#2c3e50] mb-5">
          Payment History
        </h3>
        <div className="overflow-x-auto">
        <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-[0_4px_15px_rgba(0,0,0,0.1)]">
          <thead>
            <tr>
              <th className="bg-gradient-to-r from-[#34495e] to-[#2c3e50] text-white p-4 text-left font-semibold">Session ID</th>
              <th className="bg-gradient-to-r from-[#34495e] to-[#2c3e50] text-white p-4 text-left font-semibold">Date</th>
              <th className="bg-gradient-to-r from-[#34495e] to-[#2c3e50] text-white p-4 text-left font-semibold">Gross Amount</th>
              <th className="bg-gradient-to-r from-[#34495e] to-[#2c3e50] text-white p-4 text-left font-semibold">Platform Fee</th>
              <th className="bg-gradient-to-r from-[#34495e] to-[#2c3e50] text-white p-4 text-left font-semibold">Net Amount</th>
              <th className="bg-gradient-to-r from-[#34495e] to-[#2c3e50] text-white p-4 text-left font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((payment: PaymentRecord) => (
              <tr key={payment.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                <td className="p-4 text-sm text-[#2c3e50]">{payment.session_id}</td>
                <td className="p-4 text-sm text-[#2c3e50]">{formatDate(payment.created_at)}</td>
                <td className="p-4 text-sm text-[#2c3e50]">{formatCurrency(payment.gross_amount)}</td>
                <td className="p-4 text-sm text-[#2c3e50]">{formatCurrency(payment.platform_fee)}</td>
                <td className="p-4 text-sm text-[#2c3e50]">{formatCurrency(payment.net_amount)}</td>
                <td className="p-4 text-sm">
                  <span className={`${getStatusColor(payment.payment_status)} text-white px-3 py-1 rounded-full text-xs font-semibold uppercase`}>
                    {payment.payment_status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Payout Confirmation Modal */}
      <Modal
        opened={showPayoutModal}
        onClose={() => setShowPayoutModal(false)}
        title="Confirm Payout Request"
        centered
        radius="lg"
        size="md"
      >
        <p className="text-lg text-gray-500 mb-8 leading-relaxed text-center">
          You are requesting a payout of <strong>{formatCurrency(payoutAmount)}</strong> for {pendingPayments.length} pending payment{pendingPayments.length !== 1 ? 's' : ''}.
          <br /><br />
          This action will mark all pending payments as paid and generate a payout reference.
        </p>
        <div className="flex gap-4 justify-center">
          <Button
            onClick={() => setShowPayoutModal(false)}
            variant="outline"
            color="red"
            radius="md"
            size="lg"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePayoutRequest}
            loading={loading}
            color="green"
            radius="md"
            size="lg"
          >
            Confirm Payout
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default TrainerEarningsDashboard;
